import { useState, FormEvent } from 'react'
import { MapPin, Loader2 } from 'lucide-react'

interface LocationInputProps {
  onSubmit: (postcode: string) => void
  isLoading?: boolean
}

/**
 * UK postcode validation regex
 * Matches formats like: SW1A 1AA, M1 1AA, EC1A 1BB, etc.
 */
const UK_POSTCODE_REGEX = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i

/**
 * Location input for UK postcode entry
 * Shown during first visit or when geolocation is denied
 */
export function LocationInput({ onSubmit, isLoading = false }: LocationInputProps) {
  const [postcode, setPostcode] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    const trimmed = postcode.trim()

    if (!trimmed) {
      setError('Please enter a postcode')
      return
    }

    if (!UK_POSTCODE_REGEX.test(trimmed)) {
      setError('Please enter a valid UK postcode')
      return
    }

    setError(null)
    onSubmit(trimmed)
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      {/* Icon */}
      <div className="w-16 h-16 bg-brand-100 dark:bg-brand-900 rounded-full flex items-center justify-center mb-6">
        <MapPin size={32} className="text-brand-500 dark:text-brand-400" />
      </div>

      {/* Title and description */}
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 text-center">
        Find Chargepoints Near You
      </h1>
      <p className="text-slate-600 dark:text-slate-300 text-center mb-6 max-w-sm">
        Enter your postcode to find EV chargepoints in your area
      </p>

      {/* Form */}
      <form onSubmit={handleSubmit} className="w-full max-w-sm">
        <div className="mb-4">
          <label htmlFor="postcode" className="sr-only">
            Postcode
          </label>
          <input
            id="postcode"
            type="text"
            value={postcode}
            onChange={(e) => {
              setPostcode(e.target.value.toUpperCase())
              setError(null)
            }}
            placeholder="e.g. SW1A 1AA"
            disabled={isLoading}
            className={`w-full px-4 py-3 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors
              ${
                error
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                  : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'
              }
              text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500
              disabled:opacity-50 disabled:cursor-not-allowed`}
            autoComplete="postal-code"
            autoCapitalize="characters"
          />
          {error && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 bg-brand-500 hover:bg-brand-600 disabled:bg-brand-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 min-h-[48px]"
        >
          {isLoading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Finding stations...
            </>
          ) : (
            'Find chargepoints'
          )}
        </button>
      </form>

      {/* Geolocation note */}
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-6 text-center">
        Or allow location access when prompted for automatic detection
      </p>
    </div>
  )
}
